import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useLanguage } from '../../shell/language-context';
import { actorRoleLabel } from '../../shell/actor-role-label';
import { useSettings } from '../../shell/settings-context';
import { useResource } from '../../shell/use-resource';
import { ListSkeleton } from '../../shell/skeleton';
import { ErrorState } from '../../shell/state-view';
import { formatMoney } from '@bookingdei/i18n/format-money';
import { formatClock24, formatDate } from '@bookingdei/i18n/format-date';
import type {
  AdminCatalogItemList,
  AdminRefund,
  AuditLogList,
  BookingList,
  DisputeList,
  AdminBusinessList,
  ReviewFlagList,
  SubscriptionPaymentList,
} from '../../mock/seeds/schema-types';
import type { OverviewAnalytics } from '../../mock/seeds/admin-overview-seed';
import type { Fmt } from './overview-model';
import { buildKpis, msg, num } from './overview-model';
import { buildQueues } from './overview-queues';
import type { QueueCard, QueueLists } from './overview-queues';
import { KpiCardView } from './kpi-card';
import {
  BookingsChartCard,
  FunnelCard,
  HealthStrip,
  RevenueMixCard,
  SourcesCard,
  SplitCards,
} from './overview-charts';
import './overview.css';

// Platform overview (PRD v2.5 §7.3.4 route 1, FR-A12 revised by DR-39; design
// "1 · OVERVIEW", the admin export (Super Admin .dc.html) D:631-846 + overviewVals D:9031).
// Top to bottom: six KPI cards (the first is the navy hero), revenue mix + bookings over
// time, category / city split, booking sources + the health strip, the FR-A12 funnel,
// six work queues that deep-link into their sections, and the last six audit entries.
// Reads: GET /admin/analytics?scope=overview (API-207) and the queue lists each section
// already uses — no aggregate endpoint is invented.
interface OverviewData {
  analytics: OverviewAnalytics;
  lists: QueueLists;
  recent: AuditLogList['items'];
}

async function loadOverview(signal: AbortSignal): Promise<OverviewData> {
  const [analytics, apps, svcPending, svcChanges, pay, disputes, flags, refunds, subs, audit] =
    await Promise.all([
      api.GET('/admin/analytics', {
        signal,
        params: { query: { scope: 'overview' } },
      }) as unknown as Promise<OverviewAnalytics>,
      api.GET('/admin/businesses', { signal, params: { query: { status: 'pending_approval' } } }) as Promise<AdminBusinessList>,
      api.GET('/admin/catalog-items', {
        signal,
        params: { query: { status: 'pending_review' } },
      }) as Promise<AdminCatalogItemList>,
      api.GET('/admin/catalog-items', {
        signal,
        params: { query: { status: 'changes_requested' } },
      }) as Promise<AdminCatalogItemList>,
      api.GET('/admin/bookings', {
        signal,
        params: { query: { status: 'payment_verification' } },
      }) as Promise<BookingList>,
      api.GET('/admin/disputes', {
        signal,
        params: { query: { status: 'open' } },
      }) as Promise<DisputeList>,
      api.GET('/admin/review-flags', {
        signal,
        params: { query: { status: 'open' } },
      }) as Promise<ReviewFlagList>,
      api.GET('/admin/refunds', { signal, params: { query: { status: 'pending' } } }) as Promise<{
        items: AdminRefund[];
      }>,
      api.GET('/admin/subscription-payments', {
        signal,
        params: { query: { status: 'pending' } },
      }) as Promise<SubscriptionPaymentList>,
      api.GET('/admin/audit-log', { signal }) as Promise<AuditLogList>,
    ]);
  return {
    analytics,
    lists: {
      pendingBusinesses: apps.items,
      services: [...svcPending.items, ...svcChanges.items],
      payments: pay.items,
      disputes: disputes.items,
      flags: flags.items,
      refunds: refunds.items,
      subscriptionPayments: subs.items,
    },
    recent: (audit.items ?? []).slice(0, 6),
  };
}

function QueueCardView({ q, f, onOpen }: { q: QueueCard; f: Fmt; onOpen: () => void }) {
  return (
    <button
      type="button"
      className={`ov-q ov-q--${q.tone}${q.urgent ? ' ov-q--urgent' : ''}${q.count > 0 ? ' ov-q--busy' : ''}`}
      onClick={onOpen}
    >
      <span className="ov-q__top">
        <span className="ov-q__title">
          <span className="ov-q__dot" aria-hidden="true" />
          <span className="ov-q__label">{q.label}</span>
        </span>
        <span className="ov-q__badge">{q.badge}</span>
      </span>
      <span className="ov-q__count-row">
        <span className="ov-q__count">{num(f, q.count)}</span>
        <span className="ov-q__unit">{q.unit}</span>
      </span>
      <span className="ov-q__metas">
        {q.metas.map((m) => (
          <span key={m.k} className="ov-q__meta">
            <span className="ov-q__meta-k">{m.k}</span>
            <span className={m.warn ? 'ov-q__meta-v ov-q__meta-v--warn' : 'ov-q__meta-v'}>
              {m.v}
            </span>
          </span>
        ))}
      </span>
      <span className="ov-q__foot">
        <span className="ov-q__sub">{q.sub}</span>
        <span className="ov-q__link">{q.link}</span>
      </span>
    </button>
  );
}

export function OverviewScreen() {
  const { t, lang } = useLanguage();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useResource<OverviewData>(loadOverview, []);

  const fmt = useMemo<Fmt>(() => {
    const cur = settings?.currency;
    const symbol = cur?.symbol ?? cur?.code ?? '';
    return {
      lang,
      t,
      now: new Date(),
      money: (minor: number) =>
        formatMoney(lang, minor, {
          currencySymbol: symbol,
          minorUnitScale: cur?.minor_unit_scale ?? 100,
          compact: true,
          compactDecimals: 2,
          trimZeros: false,
        }),
    };
  }, [lang, t, settings, data]);

  if (loading) return <ListSkeleton count={4} />;
  if (error || !data) return <ErrorState onRetry={reload} />;

  const a = data.analytics;
  const kpis = buildKpis(a, fmt);
  const queues = buildQueues(data.lists, fmt);

  return (
    <div className="ov">
      <div className="ov-head">
        <h1 className="ov-head__title">{t('admin.overview.title')}</h1>
        <p className="ov-head__sub">{t('admin.overview.sub')}</p>
      </div>

      <div className="ov-kpis">
        {kpis.map((k, i) => (
          <KpiCardView key={k.id} card={k} index={i} fmt={fmt} />
        ))}
      </div>

      <div className="ov-pair" id="ov-charts">
        <RevenueMixCard a={a} f={fmt} />
        <BookingsChartCard a={a} f={fmt} />
      </div>
      <SplitCards a={a} f={fmt} />
      <div className="ov-src-row">
        <SourcesCard a={a} f={fmt} />
        <HealthStrip a={a} f={fmt} />
      </div>
      <FunnelCard a={a} f={fmt} />

      <h2 className="ov-label" id="ov-queues">
        {t('admin.overview.queues')}
      </h2>
      <div className="ov-queues">
        {queues.map((q) => (
          <QueueCardView key={q.id} q={q} f={fmt} onOpen={() => navigate(q.to)} />
        ))}
      </div>

      <h2 className="ov-label" id="ov-activity">
        {t('admin.overview.recent')}
      </h2>
      <section className="ov-act" aria-labelledby="ov-activity">
        <div className="ov-act__row ov-act__row--head" aria-hidden="true">
          <div>{t('admin.audit.col.action')}</div>
          <div>{t('admin.audit.col.entity')}</div>
          <div>{t('admin.audit.col.when')}</div>
        </div>
        {data.recent.length === 0 && (
          <div className="ov-act__empty">{t('admin.overview.recent_empty')}</div>
        )}
        {data.recent.map((e) => {
          const d = new Date(e.created_at);
          return (
            <div key={e.id} className="ov-act__row">
              <div className="ov-act__main">
                <div className="ov-act__action">{e.action}</div>
                <div className="ov-act__sub" lang={e.summary ? 'en' : undefined}>
                  {e.summary ?? actorRoleLabel(t, e.actor_role)}
                </div>
              </div>
              <div className="ov-act__entity" title={e.entity_kind}>
                {e.entity_id}
              </div>
              <div className="ov-act__when">
                {msg(fmt, 'admin.overview.when', {
                  date: formatDate(lang, d),
                  time: formatClock24(lang, d.getHours(), d.getMinutes()),
                })}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}
